import { AUTH_KEYS } from "../constants/app-constants";
import { CURRENCY } from "../constants/currency";
import { LANGUAGE } from "../constants/language";
import CryptoJS from "crypto-js";

const HASH_KEY = process.env.HASH_KEY || "default_hash_key";

const hashData = (data) => {
  return CryptoJS.AES.encrypt(data, HASH_KEY).toString();
};

const unhashData = (hashedData) => {
  try {
    const bytes = CryptoJS.AES.decrypt(hashedData, HASH_KEY);
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch (error) {
    return null;
  }
};

export const getCurrentUserFromLocalStorage = () => {
  localStorage.removeItem(AUTH_KEYS.OLD_LOCAL_STORAGE_USER);
  const hashedUserValue = localStorage.getItem(AUTH_KEYS.LOCAL_STORAGE_USER);
  if (!hashedUserValue) {
    return null;
  }

  const unhashedData = unhashData(hashedUserValue);
  if (!unhashedData) {
    removeCurrentUserFromLocalStorage();
    return null;
  }

  return JSON.parse(unhashedData);
};

export const setCurrentUserInLocalStorage = (currentUser) => {
  const hashedValue = hashData(JSON.stringify(currentUser));
  localStorage.setItem(AUTH_KEYS.LOCAL_STORAGE_USER, hashedValue);
};

export const removeCurrentUserFromLocalStorage = () => {
  localStorage.removeItem(AUTH_KEYS.LOCAL_STORAGE_USER);
};

export const getCurrencyFromLocalStorage = () => {
  const storedCurrency = localStorage.getItem(AUTH_KEYS.LOCAL_STORAGE_CURRENCY);
  return storedCurrency;
};

export const setCurrencyInLocalStorage = (currency, { explicit = false } = {}) => {
  // Guard mechanism: Prevent setting USD unless explicit
  const currentStoredCurrency = localStorage.getItem(AUTH_KEYS.LOCAL_STORAGE_CURRENCY);
  if (currency === CURRENCY.USD && currentStoredCurrency && currentStoredCurrency !== CURRENCY.USD && !explicit) {
    console.warn("[setCurrencyInLocalStorage] Blocked setting USD currency without explicit flag", {
      currentStoredCurrency,
      attemptedCurrency: currency,
      explicit: explicit,
    });
    return false; // Prevent the change
  }

  localStorage.setItem(AUTH_KEYS.LOCAL_STORAGE_CURRENCY, currency);
  return true;
};

export const getI18nLanguageFromLocalStorage = () => {
  const lng = localStorage.getItem("i18nextLng");
  if (lng) {
    return lng;
  }

  const userJson = localStorage.getItem(AUTH_KEYS.LOCAL_STORAGE_USER);
  if (userJson) {
    try {
      const user = JSON.parse(userJson);
      if (user.defaultLanguage) {
        return user.defaultLanguage;
      }
    } catch { }
  }

  const pathLang = window.location.pathname.split('/')[1]?.toLowerCase();
  const supported = Object.values(LANGUAGE);
  if (pathLang && supported.includes(pathLang)) {
    return pathLang;
  }

  return LANGUAGE.ENGLISH;
};

export const setI18nLanguageInLocalStorage = (language) => {
  localStorage.setItem("i18nextLng", language);
};

export const  getI18nLanguageInLocalStorage = () => {
  return localStorage.getItem("i18nextLng");
};

export const getQuizFromLocalStorage = (quizId) => {
  return JSON.parse(localStorage.getItem(`quiz-${quizId}`));
};

export const setQuizInLocalStorage = (quizId, quiz) => {
  localStorage.setItem(`quiz-${quizId}`, JSON.stringify(quiz));
};

export const getRefreshingStateFromLocalStorage = () => {
  const refreshing = localStorage.getItem("refreshing");
  if (refreshing) {
    return JSON.parse(refreshing);
  }
  return null;
};

export const setRefreshingStateInLocalStorage = (value) => {
  localStorage.setItem("refreshing", JSON.stringify(value));
};

export const removeRefreshingStateFromLocalStorage = () => {
  localStorage.removeItem("refreshing");
};

export const setShoppingCartStateInLocalStorage = (value) => {
  localStorage.setItem("shoppingCart", JSON.stringify(value));
};

export const getShoppingCartStateInLocalStorage = () => {
  const shoppingCartState = localStorage.getItem("shoppingCart");
  if (shoppingCartState) {
    return JSON.parse(shoppingCartState);
  }
  return null;
};

export const removeShoppingCartStateFromLocalStorage = () => {
  localStorage.removeItem("shoppingCart");
};

export const setShoppingCartPaymentSuccessLocalStorage = (result) => {
  localStorage.setItem("shoppingCartPaymentSuccess", result ? "true" : "false");
};

export const getShoppingCartPaymentSuccessLocalStorage = () => {
  const data = localStorage.getItem("shoppingCartPaymentSuccess");
  if (data === "true") return true;
  if (data === "false") return false;
  return null;
};

export const removeShoppingCartPaymentSuccessLocalStorage = () => {
  localStorage.removeItem("shoppingCartPaymentSuccess");
};

export const getSmartMatchingValueFromLocalStorage = () => {
  const smartMatchingValue = localStorage.getItem("SMART_MATCHING_VALUE");
  if (smartMatchingValue) {
    try {
      return JSON.parse(smartMatchingValue);
    } catch (error) {
      //console.error('Error parsing smart matching value:', error);
      return null;
    }
  }
  return null;
};

export const setSmartMatchingValueInLocalStorage = (value) => {
  const dataToSave = {
    timestamp: new Date().toISOString(),
    answers: value,
  };
  localStorage.setItem("SMART_MATCHING_VALUE", JSON.stringify(dataToSave));
};

export const removeSmartMatchingValueFromLocalStorage = () => {
  localStorage.removeItem("SMART_MATCHING_VALUE");
};
